import { Form, Typography } from '@loophealth/loop-ui-web-library';
import React from 'react';
import {
    StyledBenefitPerLifeMember,
    StyledBenefitPerLifeMemberCheckbox,
    StyledBenefitPerLifeMemberWrapper,
    StyledBenefitPerLifeSelectionModalBody,
    StyledBenefitPerLifeSelectionModalBodyList,
    StyledBenefitPerLifeSelectionModalUncovered,
    StyledBenefitsEligibleMemberText,
    StyledDropDown
} from './styles';
import ProfileIcon from '../../atoms/ProfileIcon';
import theme from '../../../theme';
import { InfoIcon } from '../../../assets/img/CustomIcons';
import { IDropdownItem } from '../ManageModularMember/types';
import {
    capitalizeFirstLetter,
    formatAmount,
    formatFullName,
    getIconMapping
} from '../../../utils/common';
import MembersIcon from '../../../assets/img/CustomIcons/MembersIcon';
import { IAlacarte } from '../../../redux/slices/PolicyListSlice/types';
import { IBenefitPerLifeMemberConfig, IBenefitPerLifeMemberValue } from './types';

interface PurchaseAttributesStep1Props {
    plan: IAlacarte;
    planIds: string[];
    values: Record<string, IBenefitPerLifeMemberValue> ;
    setValues: (value: Record<string, IBenefitPerLifeMemberValue> ) => void;
    members: IBenefitPerLifeMemberConfig[];
    uncoveredText: string;
    hasPurchaseAttributes: boolean;
    handleCheckboxChange: (id: string, checked: boolean, dropdownOptions: IDropdownItem[]) => void;
    handleOnDropdownChange: (id: string, value: string) => void;
}

const PurchaseAttributesStep1: React.FC<PurchaseAttributesStep1Props> = ({
    plan,
    values,
    members,
    uncoveredText,
    hasPurchaseAttributes,
    handleCheckboxChange,
    handleOnDropdownChange
}) => {
    return (
        <StyledBenefitPerLifeSelectionModalBody>
            <Typography variant="small">
                {hasPurchaseAttributes && (
                    <>
                        <Typography variant="medium" weight="semiBold">
                            Step 1 :
                        </Typography>{' '}
                    </>
                )}
                Who would you like to cover under this benefit?
            </Typography>
            <StyledBenefitPerLifeSelectionModalBodyList
                $total={members.length}
                data-testid="ala-carte-per-life-selection-modal-member-list"
            >
                {members.map(({ data, dropdownOptions, disabledCheckbox }, i) => (
                    <StyledBenefitPerLifeMemberWrapper
                        data-testid={`ala-carte-per-life-selection-modal-member-${i}`}
                        key={data.id}
                    >
                        <StyledBenefitPerLifeMember>
                            <StyledBenefitPerLifeMemberCheckbox
                                type="checkbox"
                                checked={values[data.id]?.checked}
                                onChange={(e) => handleCheckboxChange(data.id, e.target.checked, dropdownOptions)}
                                disabled={disabledCheckbox}
                                data-testid={`ala-carte-per-life-selection-modal-member-${i}-checkbox`}
                            />
                            <ProfileIcon
                                icon={getIconMapping(data.relationship, data.gender)}
                                bgColor={theme.colors.coverYellow}
                            />
                            <StyledBenefitsEligibleMemberText
                                data-testid={`ala-carte-per-life-selection-modal-member-${i}-details`}
                            >
                                <Typography variant="medium" weight="semiBold">
                                    {formatFullName(data.firstName, data.lastName)}
                                </Typography>
                                <Typography variant="extraSmall" weight="medium" color="secondary">
                                    {capitalizeFirstLetter(data.relationship)}
                                </Typography>
                            </StyledBenefitsEligibleMemberText>
                        </StyledBenefitPerLifeMember>
                        {values[data.id]?.checked &&
                            (dropdownOptions.length !== 1 ? (
                                <StyledDropDown data-testid={`ala-carte-per-life-selection-member-${i}-dropdown`}>
                                    <Form.Dropdown
                                        placeholder="Select the tier"
                                        listItems={dropdownOptions}
                                        selectedItem={values[data.id]?.value ?? ''}
                                        onClick={(v) => handleOnDropdownChange(data.id, v)}
                                        autoDisplayItems={!values[data.id]?.value}
                                    />
                                </StyledDropDown>
                            ) : (
                                <Typography variant="small" weight="medium">
                                    {formatAmount(dropdownOptions[0].price)}
                                </Typography>
                            ))}
                    </StyledBenefitPerLifeMemberWrapper>
                ))}
            </StyledBenefitPerLifeSelectionModalBodyList>
            {plan.isSelfMandatory && members.length > 1 && (
                <StyledBenefitPerLifeSelectionModalUncovered
                    data-testid={`ala-carte-per-life-selection-modal-self-mandatory`}
                >
                    <InfoIcon width="20" height="20" strokeColor={theme.colors.beyondGrey} />
                    <Typography variant="small">
                        <Typography variant="small" weight="semiBold">
                            Adding Self&nbsp;
                        </Typography>
                        is mandatory to include dependants.
                    </Typography>
                </StyledBenefitPerLifeSelectionModalUncovered>
            )}
            {uncoveredText && (
                <StyledBenefitPerLifeSelectionModalUncovered
                    data-testid={`ala-carte-per-life-selection-modal-uncovered`}
                >
                    <MembersIcon />
                    <Typography variant="small">
                        <Typography variant="small" weight="semiBold">
                            {uncoveredText} not covered&nbsp;
                        </Typography>
                        in this benefit
                    </Typography>
                </StyledBenefitPerLifeSelectionModalUncovered>
            )}
        </StyledBenefitPerLifeSelectionModalBody>
    );
};

export default PurchaseAttributesStep1;
